// FLAGS: authorization
// The /login wrapper: decides when an already-signed-in scheduler is bounced off the login page, and where to.
import { Navigate, Outlet, useLocation } from "react-router";
import { useCurrentUser } from "@src/queries/auth";
import isCustomerSuccess from "@src/auth/userRoles";
import getSafeRedirectPath from "@src/utils/redirect";
import FullPageSpinner from "@src/auth/FullPageSpinner";
import ConnectionError from "@src/auth/ConnectionError";

/**
 * FLAGS: authorization
 * Only CustomerSuccess users are redirected away. Dropping that role test would
 * send a signed-in non-CS account to the shell's AccessDenied screen instead of
 * the login page's own NotPermitted, losing the sign-in-as-someone-else path.
 * Wraps /login: an already-authenticated CustomerSuccess user is sent back
 * to where they came from (validated, relative-only) or to the events list.
 */
const PublicOnlyRoute = () => {
  const location = useLocation();
  const {
    data: user,
    isPending,
    isError,
    refetch,
    isFetching,
  } = useCurrentUser();

  if (isPending) {
    return <FullPageSpinner />;
  }

  // A signed-out session resolves to null rather than an error (getUserProfile maps 401/403), so an
  // error here means the API is unreachable — and the login page it would otherwise render calls
  // that same API to sign in.
  if (isError) {
    return (
      <ConnectionError onRetry={() => refetch()} isRetrying={isFetching} />
    );
  }

  if (user && isCustomerSuccess(user)) {
    const from = getSafeRedirectPath(
      (location.state as { from?: string } | null)?.from,
    );
    return <Navigate to={from ?? "/events"} replace />;
  }

  return <Outlet />;
};

export default PublicOnlyRoute;
